// Merge del acta estructurada que devuelve la IA (endpoints /api/structure-meeting-acta
// y /api/process-meeting-acta-audio) al estado del editor. La IA trabaja con NOMBRES de
// copropietarios y ETIQUETAS de unidad; acá los resolvemos a los ids del padrón y armamos
// el objeto `acta` que consume ActaTemplate/ActaForm. El motor de votación calcula las
// mayorías por alícuota sobre el resultado.

import { defaultActa, emptyVotacion, uid, externoIdDesdeNombre, esVotanteExterno } from "./actaConfig";

// Normaliza para matchear nombres: minúsculas, sin acentos, sin puntuación extra.
function norm(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Construye un resolvedor nombre -> copropietarioId tolerante (exacto, luego "contiene").
function makeCoproResolver(copropietarios) {
  const byNorm = new Map();
  for (const c of copropietarios || []) byNorm.set(norm(c.nombre), c);
  return (nombre) => {
    if (!nombre) return null;
    const n = norm(nombre);
    if (!n) return null;
    if (byNorm.has(n)) return byNorm.get(n).id;
    // Match parcial: el nombre de la IA contiene o está contenido en uno del padrón.
    for (const c of copropietarios || []) {
      const cn = norm(c.nombre);
      if (cn && (cn.includes(n) || n.includes(cn))) return c.id;
    }
    // Match por apellido / primera palabra significativa.
    const token = n.split(" ")[0];
    for (const c of copropietarios || []) {
      if (norm(c.nombre).split(" ").includes(token)) return c.id;
    }
    return null;
  };
}

// Resolvedor etiqueta -> unidadId.
function makeUnidadResolver(unidades) {
  const byEtq = new Map();
  for (const u of unidades || []) byEtq.set(norm(u.etiqueta), u);
  return (etq) => {
    const n = norm(etq);
    if (byEtq.has(n)) return byEtq.get(n).id;
    for (const u of unidades || []) {
      if (norm(u.etiqueta) === n) return u.id;
    }
    return null;
  };
}

const TIPO_OK = new Set(["ordinaria", "extraordinaria"]);
const CONV_OK = new Set(["primera", "segunda"]);
const RESOL_OK = new Set(["mayoria", "unanime", "informativa"]);

/**
 * Combina el acta de la IA con el padrón y (opcionalmente) un acta base.
 *
 * @param {object} ai      - JSON devuelto por el backend (acta).
 * @param {object} padron  - { edificio, copropietarios }
 * @param {object} base    - acta base a respetar (id, edificioNombre, etc.). Opcional.
 * @returns {{ acta: object, stats: object }}
 */
export function mergeAiActa(ai, padron, base = null) {
  const edificio = padron?.edificio || { unidades: [] };
  const copropietarios = padron?.copropietarios || [];
  const resolverCopro = makeCoproResolver(copropietarios);
  const resolverUnidad = makeUnidadResolver(edificio.unidades);

  const baseActa = base ? { ...base } : defaultActa(edificio);
  const ai_ = ai || {};
  const stats = { unidadesNoMapeadas: [], personasNoMapeadas: new Set(), puntos: 0 };

  // ── Campos escalares ──
  const next = {
    ...baseActa,
    tipo: TIPO_OK.has(ai_.tipo) ? ai_.tipo : baseActa.tipo,
    convocatoria: CONV_OK.has(ai_.convocatoria) ? ai_.convocatoria : baseActa.convocatoria,
    fecha: ai_.fecha || baseActa.fecha,
    ciudad: ai_.ciudad || baseActa.ciudad,
    horaApertura: ai_.horaApertura || baseActa.horaApertura,
    horaCierre: ai_.horaCierre || baseActa.horaCierre,
    actaNumero: ai_.actaNumero || baseActa.actaNumero,
    folio: ai_.folio || baseActa.folio,
    leadPortada: ai_.leadPortada || baseActa.leadPortada,
    textoApertura: ai_.textoApertura || baseActa.textoApertura,
    textoCierre: ai_.textoCierre || baseActa.textoCierre,
  };

  // ── Asistencia: una fila por unidad del padrón, override con lo de la IA ──
  const aiAsisByUnidad = new Map();
  for (const row of ai_.asistencia || []) {
    const uId = resolverUnidad(row.unidad);
    if (uId) aiAsisByUnidad.set(String(uId), row);
    else if (row.unidad) stats.unidadesNoMapeadas.push(row.unidad);
  }
  next.asistencia = (edificio.unidades || []).map((u) => {
    const aiRow = aiAsisByUnidad.get(String(u.id));
    if (!aiRow) {
      return { unidadId: u.id, presente: false, votanteId: u.titularId || "", votanteNombre: "", nota: "" };
    }
    let votanteId = u.titularId || "";
    let votanteNombre = "";
    if (aiRow.votante) {
      const vId = resolverCopro(aiRow.votante);
      if (vId) {
        votanteId = vId;
      } else {
        // No es copropietario del padrón: apoderado externo (texto libre).
        votanteId = externoIdDesdeNombre(aiRow.votante, `u${u.id}`);
        votanteNombre = String(aiRow.votante);
      }
    }
    return {
      unidadId: u.id,
      presente: !!aiRow.presente,
      votanteId,
      votanteNombre,
      nota: aiRow.nota || "",
    };
  });

  // Resolver de votos: copropietario del padrón o apoderado externo ya cargado
  // en la asistencia (por nombre normalizado).
  const extByNombre = new Map();
  for (const row of next.asistencia) {
    if (esVotanteExterno(row.votanteId) && row.votanteNombre) {
      extByNombre.set(norm(row.votanteNombre), row.votanteId);
    }
  }
  const resolverPersona = (nombre) => resolverCopro(nombre) || extByNombre.get(norm(nombre)) || null;

  // ── Orden del día ──
  const puntos = (ai_.ordenDelDia || []).map((p, i) => {
    const metodo = p.metodo === "coeficiente" ? "coeficiente" : "unidades";
    const tipoResolucion = RESOL_OK.has(p.tipoResolucion) ? p.tipoResolucion : "mayoria";
    const votosPorPersona = { favor: [], contra: [], abstencion: [] };
    const aiVotos = p.votos || {};
    for (const sentido of ["favor", "contra", "abstencion"]) {
      for (const nombre of aiVotos[sentido] || []) {
        const pid = resolverPersona(nombre);
        if (pid) votosPorPersona[sentido].push(String(pid));
        else stats.personasNoMapeadas.add(nombre);
      }
    }
    return {
      id: uid("pt"),
      numero: i + 1,
      seccion: p.seccion || "",
      titulo: p.titulo || "",
      cuerpo: p.cuerpo || "",
      tipoResolucion,
      textoResolucion: p.textoResolucion || "",
      votacion:
        tipoResolucion === "informativa"
          ? emptyVotacion()
          : { metodo, votosPorPersona },
    };
  });
  next.ordenDelDia = puntos.length ? puntos : baseActa.ordenDelDia;
  stats.puntos = puntos.length;

  // ── Firmas ──
  if (ai_.presidencia) {
    const pid = resolverCopro(ai_.presidencia);
    if (pid) next.presidenciaId = pid;
    else next.presidenciaNombre = ai_.presidencia;
  }
  if (ai_.secretaria) {
    const sid = resolverCopro(ai_.secretaria);
    if (sid) next.secretariaId = sid;
    else next.secretariaNombre = ai_.secretaria;
  }

  stats.personasNoMapeadas = [...stats.personasNoMapeadas];
  return { acta: next, stats };
}

/** Payload de padrón liviano para mandar al backend (unidades + nombres). */
export function padronPayload(padron) {
  const unidades = (padron?.edificio?.unidades || []).map((u) => {
    const titular = (padron.copropietarios || []).find((c) => String(c.id) === String(u.titularId));
    return {
      etiqueta: u.etiqueta,
      alicuota: typeof u.alicuota === "number" ? u.alicuota : null,
      titular: titular?.nombre || "",
    };
  });
  const copropietarios = (padron?.copropietarios || []).map((c) => c.nombre);
  return { units: unidades, copropietarios };
}
